// src/helpers/videoQrCodes.js
//
// Embedded video players print as empty black boxes. On the PDF pages, the
// v-video-qr directive swaps each player in v-html content for a QR code of
// the video's address, so a printed copy can still reach the video (#127).
// The online unit page keeps the player.

import QRCode from 'qrcode'
import { i18n } from '@/plugins/i18n'

// Players embed a URL meant for an iframe; scanning should open the normal
// watch page instead.
export function videoWatchUrl(src) {
  if (!src) return null
  let url
  try {
    url = new URL(src, window.location.origin)
  } catch (e) {
    return null
  }
  if (!/^https?:$/.test(url.protocol)) return null

  const host = url.hostname.replace(/^www\./, '')
  const youtube = url.pathname.match(/^\/embed\/([\w-]+)/)
  if (/(^|\.)youtube(-nocookie)?\.com$/.test(host) && youtube) {
    return `https://www.youtube.com/watch?v=${youtube[1]}`
  }
  const vimeo = url.pathname.match(/^\/video\/(\d+)/)
  if (host === 'player.vimeo.com' && vimeo) {
    // unlisted videos need their hash
    const hash = url.searchParams.get('h')
    return `https://vimeo.com/${vimeo[1]}${hash ? `/${hash}` : ''}`
  }
  return url.href
}

async function replacePlayers(el) {
  const players = el.querySelectorAll('iframe')
  for (const player of players) {
    const watchUrl = videoWatchUrl(player.getAttribute('src'))
    if (!watchUrl) continue

    let dataUrl = null
    try {
      dataUrl = await QRCode.toDataURL(watchUrl, { margin: 1, width: 240 })
    } catch (e) {
      // print the address on its own instead
      console.warn('Could not make a QR code for a mission video', e)
    }
    // v-html may have replaced the content while the code was generating
    if (!player.isConnected) continue

    const figure = document.createElement('figure')
    figure.className = 'mission-video-qr'
    if (dataUrl) {
      const img = document.createElement('img')
      img.src = dataUrl
      img.alt = i18n.global.t('unitStudy.videoQr.alt')
      figure.appendChild(img)
    }
    const caption = document.createElement('figcaption')
    caption.textContent = i18n.global.t(
      dataUrl ? 'unitStudy.videoQr.caption' : 'unitStudy.videoQr.urlOnly'
    )
    const link = document.createElement('span')
    link.className = 'mission-video-qr__url'
    link.textContent = watchUrl
    caption.appendChild(link)
    figure.appendChild(caption)
    player.replaceWith(figure)
  }
}

export const videoQr = {
  mounted: replacePlayers,
  updated: replacePlayers,
}
